import {classNames, observer, pluralize, useAsObservableSource, useCallback, useLocalStore, utils} from '@api'
import {Classes, Icon, IconName, IMenuItemProps, Menu, MenuDivider, MenuItem} from '@blueprintjs/core'
import {Tooltip2} from '@api'
import {
  CatalogColumnSliderMaterial, CatalogImageSourceSelectedWidgetsMenuItems, CatalogViewModeContextMenuItem, ChooseSubgroupSubmenu, ChooseSwatchesSubmenuItem, CustomCardViewPresetMenuItems,
  GroupByDropdownStoreContext, useVipLocalStore, GroupSubtitlesContextMenuItem, ImageSourcesMenuContent, ManageGroupByHierarchyMenuItem, SelectedCatalogItemsActionMenuItems, useGroupByDropdownStore, useGroupByGroupStore, useGrouping, useStores, VipLocalStore, WorkspaceChooserMenuItems, StoresInstanceContext, useFlexLayoutContextMenuStore, FlexLayoutContextMenu, layoutSettingDescriptors, useBuilderCardStore, AssortmentLayoutContextMenuItem, SwitchFlexCatalogRootViewModeMenuItems
} from '@design-board'
import {Slider, Tooltip} from '@material-ui/core'
import {Mark} from '@material-ui/core/Slider/Slider'
import {Dashboard, SelectAll, ViewComfy, ZoomInSharp, ZoomOutSharp} from '@material-ui/icons'
import {BuilderCardStore, GroupByDropdownStore, GroupMenuItems} from '@modules/catalog'
import {TooltipLocation, GroupByGrouping, ShowToolbarItemsSettings, ICatalogFieldConfig, useCatalog, globalStores, site} from '@stores'
import {CreateLinkedTabWindowMenuItem} from '@stores/catalog/flexLayout'
import {useVipFlexLayoutTabStore, VipFlexLayoutTabStoreContext} from '@stores/catalog/flexLayout/flexComponents/VipFlexLayoutTab'
import {CentricLogo, icons} from '@stores/ui'
import {CollapsibleStateful, PrimaryIconButton} from 'centric-ui-components'
import {action, computed} from 'mobx'
import React, {Fragment, useEffect} from 'react'
import {Kbd} from '~components/Kbd'
import * as css from './CatalogContextMenu.css'
import {DevCatalogContextMenuItems} from './DevCatalogContextMenuItems'

export interface IContextMenuItems {
  contextMenuItems: React.ReactNode
}

export const CatalogContextMenu: React.FunctionComponent<{}> & { css?: typeof css } = observer<{}>(function CatalogContextMenu() {
  const {contextMenu, contextMenu: {store, contextMenuProps}, flexLayout: {activeTab}} = useStores()
  const content = store?.contextMenuItems ?? <DefaultCatalogContextMenuItems/>

  useEffect(() => {
    return () => {
      // console.debug(`Context menu closing`)
      contextMenu.onClosed()
    }
  }, [])

  // console.debug(`Show ContextMenu for `, {store})

  const injectStores = !content ? null : store?.stores ?? activeTab?.vipTabStore?.storesToInject ?? globalStores
  return (!content ? null :
          <div className={classNames(css.root, contextMenuProps.className)}
               style={injectStores?.catalog?.rootStyle as any}
               onDoubleClick={utils.eatMouseEvent}
               data-theme='light' data-context-item={contextMenu.contextType}>
            <StoresInstanceContext.Provider value={injectStores}>
              <VipFlexLayoutTabStoreContext.Provider value={store?.stores?.catalogTab ?? activeTab?.vipTabStore}>
                <Menu className={css.rootMenu} data-type={contextMenu.rootMenuAttribute}>
                  {content}
                </Menu>
              </VipFlexLayoutTabStoreContext.Provider>
            </StoresInstanceContext.Provider>
          </div>

  )
})

CatalogContextMenu.css = css

export type ContextMenuItemProps = {
  isDefaultAction?: boolean,
  noSubmenuPadding?: boolean,
  dividerAfter?: boolean,
  dividerBefore?: boolean

  strikethrough?: boolean

  /**
   * Show a 'tick' if true and a 'blank' if false
   */
  active?: boolean
  rightIcon?: IconName | JSX.Element

  /**
   * Keyboard shortcut
   */
  kbd?: string
} & Partial<IMenuItemProps> & React.AnchorHTMLAttributes<HTMLAnchorElement>

export const ContextMenuItem = observer(function ContextMenuItem({strikethrough, dividerBefore, dividerAfter, noSubmenuPadding, isDefaultAction, popoverProps, className, rightIcon, kbd, ...props}: ContextMenuItemProps) {
  return <Fragment>
    {dividerBefore && <MenuDivider/>}
    <MenuItem multiline={true}
              shouldDismissPopover={false}
              labelElement={<Fragment>
                {rightIcon ?
                 _.isString(rightIcon) ? <Icon className={css.rightIcon} icon={rightIcon}/>
                                       : <div className={css.rightIcon}>{rightIcon}</div>
                           : _.isBoolean(props.active) && props.icon != null
                             ? undefined // <Icon className={css.rightIcon} icon={props.active ? 'tick' : 'blank'}/>
                             : undefined}
                {kbd && <Kbd variant='menu' children={kbd}/>}
              </Fragment>}
              {...props}
              icon={_.isBoolean(props.active) ? props.active ? 'tick' : (props.icon ?? 'blank') : props.icon}
              active={undefined}
              className={classNames(css.contextMenuItem, className, {[Classes.POPOVER_DISMISS]: props.shouldDismissPopover})}
              data-default-action={isDefaultAction === true}
              data-strikethrough={strikethrough}
              popoverProps={{...(popoverProps ?? {hoverCloseDelay: 400}), ...(noSubmenuPadding ? {className: classNames(css.noSubmenuPadding, popoverProps?.className)} : {})}}
              onClick={props.onClick} //  ?? (props.href && !noEatHrefMouseClick ? utils.eatMouseEvent : undefined) // need consumers to decide to eat the mouse event themselves
    />
    {dividerAfter && <MenuDivider/>}
  </Fragment>
  // /*props.shouldDismissPopover && popover2Classes.POPOVER2_DISMISS*/)
})

/**
 * YFTS-7995
 */
export const DefaultCatalogContextMenuItems = observer<{ hideClearHierarchy?: boolean, hideGroupBy?: boolean, hideNewItem?: boolean }>(function DefaultCatalogContextMenuItems(p) {
  const {catalog, contextMenu, isGlobalStore} = useStores(),
    {strings, workspaces, isProductView, isGroupedView, isShowingAssortment, settings: {viewMode, levelOfDetail}, onCycleLevelOfDetail} = catalog,
    {contextType} = contextMenu,
    flexLayoutContextMenuStore = useFlexLayoutContextMenuStore(),
    tabStore = useVipFlexLayoutTabStore(),
    card = useBuilderCardStore(),
    hideGroupBy = p.hideGroupBy,
    isCatalog = tabStore?.tab?.component === 'catalog',
    isSeasonSelector = contextType === 'seasonSelector'

  return (
    <Fragment>
      {!isGlobalStore && <Fragment>
        {isCatalog && <Fragment>
          {!card && <SelectedCatalogItemsActionContextMenuItem/>}

          {contextType != 'seasonSelector' &&
		   <Fragment>
			   <MenuDivider/>

			   <ManageFieldsContextMenuItem/>

             {tabStore?.tab?.catalogRoot && (tabStore?.tab?.catalogRoot.catalog.arePdfTemplatesAvailable) &&
              <ContextMenuItem icon={icons.card} text='View Mode'>
				  <SwitchFlexCatalogRootViewModeMenuItems catalogRootTab={tabStore.tab.catalogRoot}/>
			  </ContextMenuItem>}

             {!hideGroupBy && isProductView && <Fragment>
				 <GroupByCreateLevelMenuItem/>

               {/*<FilterBySubmenu />*/}
               {/*<SortBySubmenu />*/}
				 <MenuDivider/>
			 </Fragment>}


             {!isSeasonSelector && <Fragment>
               {!p.hideNewItem && <NewCatalogItemSubmenu dividerBefore dividerAfter/>}

               {!hideGroupBy && isGroupedView && <Fragment>
				   <ManageGroupByHierarchyMenuItem/>
				   <MenuDivider/>
			   </Fragment>}
			 </Fragment>}
             {/*{!hideGroupBy && !p.hideClearHierarchy && <ClearHierarchyMenuItem/>}*/}

             {/*<CatalogHierarchySubmenu/>*/}


             {false && isProductView && <ContextMenuItem icon={levelOfDetail ? 'zoom-out' : 'zoom-in'} text={`${strings.levelOfDetail(levelOfDetail)} (Catalog)`} onClick={onCycleLevelOfDetail}/> // Very expensive right now, revisit after improving LOD toggle performance
             }
		   </Fragment>}

			<ExpandedCardsContextMenuItems/>


          {(workspaces.detailAggregations?.length > 0) && <Fragment><MenuDivider/><AggregateDetailsContextMenuItem/></Fragment>}
			<TargetsToolbarContextMenuItem/>

			<MenuDivider/>

			<ToggleLeftSidebarContextMenuItem/>
          {!flexLayoutContextMenuStore && <SwitchWorkspacesMenuItem/>}
		</Fragment>}
	  </Fragment>}

      {false && DEBUG && <ToolbarItemsContextMenuItem/>}

      <CreateLinkedTabWindowMenuItem/>

      {DEBUG && <DevCatalogContextMenuItems/>}
      {/*<SwitchWorkspacesMenuItem/>*/}

      {/*<MenuDivider />*/}
      {/*<ContextMenuItem icon='blank' text='' onClick={undefined}/>*/}

      {/*<MenuDivider />*/}
    </Fragment>)
})

export const ShowEmptyGroupsMenuItem = observer(function ShowEmptyGroupsMenuItem() {
  const {settings, settings: {groupBy: {showEmptyGroups}}} = useCatalog()
  return (<ContextMenuItem text={'Show Empty Groups'} icon={showEmptyGroups ? 'tick' : 'blank'} onClick={() => settings.groupBy.showEmptyGroups = !showEmptyGroups}/>)
})

export const ShowOnlyExpandedGroupsMenuItem = observer(function ShowOnlyExpandedGroupsMenuItem() {
  const {groupBy: {settings, settings: {showOnlyExpanded}}} = useCatalog()
  return (<ContextMenuItem text={'Hide Collapsed Groups'} icon={!showOnlyExpanded ? 'tick' : 'blank'} onClick={() => settings.showOnlyExpanded = !showOnlyExpanded}/>)
})
export const ShowExpandedGroupsFirstMenuItem = observer(function ShowExpandedGroupsFirstMenuItem() {
  const {groupBy: {settings, settings: {showExpandedFirst}}} = useCatalog()
  return (<ContextMenuItem text={'Show Expanded Groups First'} icon={showExpandedFirst ? 'tick' : 'blank'} onClick={() => settings.showExpandedFirst = !showExpandedFirst}/>)
})

export const ColumnViewMenuItem = observer(function ColumnViewMenuItem() {
  const {groupBy: {settings, settings: {columnView}}} = useCatalog()
  return (<ContextMenuItem text={'Column View'} icon={columnView ? 'tick' : icons.columnView} onClick={() => settings.columnView = !columnView}/>)
})

export const PresetCardViewsCurrentWorkspaceMenuItem = observer<ContextMenuItemProps>(function PresetViewsCurrentWorkspaceMenuItem(props) {
  const {cv: {customCardViews}} = useCatalog()

  return _.isEmpty(customCardViews) ? null :
         <ContextMenuItem {...props} icon='prescription' text='Field Presets'>
           <CustomCardViewPresetMenuItems/>
         </ContextMenuItem>
})

export const CatalogSettingsContextMenuItem = observer(function CatalogSettingsContextMenuItem() {
  const {workspaces} = useStores()
  return (
    <ContextMenuItem text={`${workspaces.workspaceConfig?.title} Catalog`} icon='cog'>
      <DefaultCatalogContextMenuItems/>
    </ContextMenuItem>
  )
})

export const HeroCardsForGroupingLevelFieldContextMenuItem = observer<{ field: ICatalogFieldConfig }>(function LibrarySubgroupForGroupingLevelContextMenuItem({field}) {
  const obsProps = useAsObservableSource({field}),
    grouping = useGrouping(),
    {groupBy: {hierarchyGroups}, labelForField} = useCatalog(),
    {onSelectField, active} = useLocalStore(() => new HeroCardsForGroupingLevelFieldContextMenuItemStore(obsProps, grouping))

  // Don't show a subgroup menu option for a group in the hierarchy
  return <ContextMenuItem key={field.field}
                          text={labelForField(field)}
                          disabled={hierarchyGroups.some(hg => hg?.fieldDefinition?.field == field.field)}
                          active={active}
                          onClick={onSelectField}
  />
})

class HeroCardsForGroupingLevelFieldContextMenuItemStore {
  constructor(public props: { field: ICatalogFieldConfig }, public group: GroupByGrouping) {

  }

  @action onSelectField = () => {
    const {props: {field}, active, group: {settingsForLevel, settings, groupDefinition}} = this

    if (active) {
      settingsForLevel.heroCardField = null
    } else {
      settingsForLevel.heroCardField = field.field
      // Don't show the library in both swatches and hero view
      settings.swatches = (settings.swatches ?? []).filter(s => _.isString(s) ? s != field.field : s?.field != field.field)
    }
  }

  @computed get active() {
    const {props: {field}, group} = this

    return group.settingsForLevel.heroCardField === field.field
  }
}

export const SpecSwatchForGroupingLevelContextMenuItem = observer<{ grouping: GroupByGrouping, field: ICatalogFieldConfig }>(function SpecSwatchForGroupingLevelContextMenuItem({grouping, field}) {
  const obsProps = useAsObservableSource({field}),
    catalog = useCatalog(),
    {onToggleSwatchDisplay, active} = useLocalStore(() => new SpecSwatchContextMenuItemStore(obsProps, grouping)),
    label = catalog.labelForField(field)

  return <ContextMenuItem
    text={label}
    active={active}
    onClick={onToggleSwatchDisplay}
  />
})

class SpecSwatchContextMenuItemStore {
  constructor(public props: { field: ICatalogFieldConfig }, public group: GroupByGrouping) {

  }

  @action onToggleSwatchDisplay = () => {
    const {props: {field: f}, active, group: {catalog, settingsForLevel: {swatches}, settingsForLevel}} = this,
      {groupBy} = catalog
    if (active) {
      settingsForLevel.swatches = (swatches ?? []).filter(({field}) => field !== f.field)
    } else {
      settingsForLevel.swatches = [...(swatches ?? []), {field: f.field}]
      groupBy.settings.showSwatches = true
    }
  }

  @computed get active() {
    if (!this.group) return false

    const {props: {field: {field}}, group: {settings}} = this

    return settings?.swatches?.some(s => _.isString(s) ? field === s : s.field === field) ?? false
  }
}

export const GroupByContextMenuItems = observer(function GroupByContextMenuItems() {
  const {isProductView, groupBy} = useCatalog()

  return !isProductView && <Fragment>
    {/*<MenuDivider title={strings.groupBy}/>*/}
    {/* Change the current grouping */}
	  <CatalogHierarchyMenuItems/>
	  <GroupSubtitlesContextMenuItem group={groupBy.firstGroup}/>
	  <ChooseSubgroupSubmenu/>
	  <ChooseSwatchesSubmenuItem/>
	  <MenuDivider/>
	  <CollapseAllGroupsMenuItem/>
	  <ExpandAllGroupsMenuItem/>
	  <MenuDivider/>
	  <FlatModeMenuItem/>
	  <ColumnViewMenuItem/>
	  <ShowExpandedGroupsFirstMenuItem/>
	  <ShowOnlyExpandedGroupsMenuItem/>
	  <ShowEmptyGroupsMenuItem/>
  </Fragment>
})

/*
<Tooltip2 content={strings.flatMode} placement='bottom'>
			           <PrimaryIconButton onClick={() => groupBy.settings.groupView = groupView == 'flat' ? 'card' : 'flat'} active={groupView == 'flat'}><Icon icon='th-list'/></PrimaryIconButton>
		           </Tooltip2>
 */
const FlatModeMenuItem = observer(function FlatModeMenuItem() {
  const {strings, groupBy: {cardModeAvailable, settings, settings: {groupView}}} = useCatalog()

  return (cardModeAvailable && <ContextMenuItem icon={groupView === 'flat' ? 'tick' : 'th-list'}
                                                text={strings.flatMode}
                                                onClick={() => settings.groupView = groupView == 'flat' ? 'card' : 'flat'}
  />)
})
const CollapseAllGroupsMenuItem = observer(function CollapseAllGroupsMenuItem() {
  const {groupBy} = useCatalog()
  return groupBy.isGroupedView && <ContextMenuItem icon='double-chevron-up' text='Collapse All' onClick={() => groupBy.collapseAll(true)}/>
})

const ExpandAllGroupsMenuItem = observer<ContextMenuItemProps>(function ExpandAllGroupsMenuItem(props) {
  const {groupBy} = useCatalog()
  return (groupBy.isGroupedView && <ContextMenuItem {...props} icon='double-chevron-down' text='Expand All' onClick={groupBy.expandAll}/>)
})

export const TargetsToolbarContextMenuItem = observer(function TargetsToolbarContextMenuItem() {
  const {onToggleShowTargetsToolbar, targetsToolbarConfig, settings: {showTargetsToolbar}} = useCatalog()
  return targetsToolbarConfig ? <Fragment>
    <ContextMenuItem icon={icons.targetsToolbar} text='Targets Toolbar' active={showTargetsToolbar} onClick={onToggleShowTargetsToolbar}/>
  </Fragment> : null
})

export const FilterBySubmenu = observer(function FilterBySubmenu() {
  const {filter: {filtersWithSelections, filterFields, selectAndExpandFilterGroup}, labelForField, labelForFieldName_currentCatalog} = useCatalog()
  return (
    <ContextMenuItem icon={icons.filter} text={`Filter By...`}>
      {filtersWithSelections.map(f => <ContextMenuItem key={f.group} icon='tick' text={labelForFieldName_currentCatalog(f.group)}/>)}
      {filterFields.map(f => <ContextMenuItem key={f.field} icon='blank' text={labelForField(f)} onClick={() => selectAndExpandFilterGroup(f.field)}/>)}
    </ContextMenuItem>
  )
})

export const SortBySubmenu = observer(function SortBySubmenu() {
  const {sortableFields, labelForField} = useCatalog()
  return (
    <ContextMenuItem icon={icons.sort} text={`Sort By...`}>
      {sortableFields.map(f => <ContextMenuItem key={f.field} icon='blank' text={labelForField(f)}/>)}
    </ContextMenuItem>
  )
})

export const NewCatalogItemSubmenu = observer<ContextMenuItemProps>(function NewCatalogItemSubmenu(props) {
  const {catalogItemName, canCreateItem, createItem, strings, stores: {catalog}} = useCatalog(),
    groupStore = useGroupByGroupStore()
  if (!canCreateItem || !createItem) return null

  const {onCreateItem, onCreateItems, onCreateItemFromImages} = createItem

  return canCreateItem && (
    <ContextMenuItem {...props} icon={icons.add} text={`${strings.new} ${pluralize(catalogItemName, 1)}...`} onClick={groupStore?.onCreateItem ?? onCreateItem}>
      <ContextMenuItem icon={icons.add} text={strings.addCatalogItem(catalog)} onClick={groupStore?.onCreateItem ?? onCreateItem}/>
      {!groupStore && <Fragment>
		  <ContextMenuItem icon={icons.addMany} text={`${pluralize(strings.addCatalogItem(catalog))}...`} onClick={groupStore?.onCreateItems ?? onCreateItems}/>
		  <ContextMenuItem icon={icons.addFromImages} text={`Add ${catalogItemName} from Image(s)...`} onClick={e => groupStore?.onCreateItemFromImages ?? onCreateItemFromImages(true)}/>
	  </Fragment>}
    </ContextMenuItem>
  )
})

export const ExpandedCardsContextMenuItems = observer(function f() {
  const {offlineItemsOfCurrentWorkspace, settings: {viewMode}} = useCatalog()
  if (_.isEmpty(offlineItemsOfCurrentWorkspace) || viewMode != 'card') return null

  return null

  return (
    <Fragment>
      <MenuDivider/>
      <ContextMenuItem icon='pin' text={`Only Show Expanded Cards (${offlineItemsOfCurrentWorkspace.length})`} onClick={undefined}/>
      <ContextMenuItem icon='collapse-all' text={`Collapse Expanded Cards (${offlineItemsOfCurrentWorkspace.length})`} onClick={undefined}/>
    </Fragment>)
})
export const SwitchWorkspacesMenuItem = observer<{ showCurrentWorkspace?: boolean }>(function SwitchWorkspacesContextMenu({showCurrentWorkspace}) {
  const {catalog: {itemCount, workspaceConfig}, flexLayout: {flexEnabled, isMaximized}} = useStores()
  if ((flexEnabled && !isMaximized) || !workspaceConfig) return null

  return (
    <ContextMenuItem icon='applications' text={showCurrentWorkspace ? `${workspaceConfig?.title} (${itemCount.toLocaleString()})` : 'Switch to Workspace...'}>
      <WorkspaceChooserMenuItems/>
    </ContextMenuItem>
  )
})

export const BuilderCardSpecItemMenuItems = observer<{ store: BuilderCardStore }>(function BuilderCardSpecItemMenuItems({store}) {
  return (
    <Fragment>
      <ContextMenuItem text='BuilderCardSpecItem'/>
    </Fragment>
  )
})

/// region Product Card Specific

export const CardSpacingSliderCatalog = observer<{ className?: string }>(function CardSpacingSliderCatalog({className}) {
  const {settings: {viewMode, cardSpacing}, minCardSpacing: min, maxCardSpacing: max, resetCardSpacing, onSetCardSpacing_debounced, isShowingClientPDF, assortments, componentStores} = useCatalog()

  if (assortments?.hideCatalogSpecificWidgets) {
    return null
  }

  return ((viewMode === 'card' && isShowingClientPDF !== true)) && <CardSizeSlider className={className}
                                                                                   value={cardSpacing}
                                                                                   min={min} max={max}
                                                                                   onChange={onSetCardSpacing_debounced}
                                                                                   onDoubleClick={resetCardSpacing} tooltipTitle='Card Spacing' zoomInIcon={<SelectAll/>}
                                                                                   zoomOutIcon={<ViewComfy/>}/>
})

export const CardSizeSliderCatalog = observer<{ className?: string }>(function CardSizeSliderCatalog({className}) {
  const {cardSize, settings: {viewMode}, componentStores, minCardSize, maxCardSize, onSetCardSize_throttled, resetCardSize, isShowingClientPDF, assortments} = useCatalog()

  if (assortments?.hideCatalogSpecificWidgets) {
    return null
  }

  return (viewMode === 'card' && isShowingClientPDF !== true) &&
         <CardSizeSlider className={className} value={cardSize} min={minCardSize} max={maxCardSize} onChange={onSetCardSize_throttled} onDoubleClick={resetCardSize} tooltipTitle='Card Size'/>
})

export const AssortmentCardSizeSliderCatalog = observer<{ className?: string }>(function AssortmentCardSizeSliderCatalog({className}) {
  const {settings: {assortment: {layout: {rootFontSize}}}, assortments: {selectedAssortment, onSetSetting_, resetSetting}, isShowingClientPDF} = useCatalog()

  return (selectedAssortment != null && isShowingClientPDF !== true) &&
         <CardSizeSlider
	         className={className} value={rootFontSize}
             {...layoutSettingDescriptors.rootFontSize}
	         onChange={(e, v: number) => onSetSetting_('rootFontSize', v)}
	         onDoubleClick={() => resetSetting('rootFontSize')} tooltipTitle={`Assortment Size (${rootFontSize})`}
	         zoomInIcon={<Dashboard className={css.zoomInAssortment}/>}
	         zoomOutIcon={<Dashboard className={css.zoomOutAssortment}/>}
         />
})

export interface CardSizeSliderProps {
  className?: string
  value?: number
  defaultValue?: number
  min?: number
  max?: number
  onChange?: (event: React.ChangeEvent<{}>, value: number | number[]) => void
  onDoubleClick?: React.MouseEventHandler
  tooltipTitle?: string
  zoomOutIcon?: React.ReactElement<any>
  zoomInIcon?: React.ReactElement<any>
  tooltipLocation?: TooltipLocation
  step?: number
  hideIcons?: boolean
  marks?: boolean | Mark[]
  valueLabelDisplay?: 'on' | 'auto' | 'off'
}

export const CardSizeSlider: React.FunctionComponent<CardSizeSliderProps> & { css?: typeof css } = observer<CardSizeSliderProps>(function CardSizeSlider({hideIcons, tooltipLocation, step, zoomInIcon, zoomOutIcon, className, onDoubleClick, tooltipTitle, ...props}) {
  const percentage = props.value / (props.max - props.min),
    stepDown = step ?? Math.ceil((props.max - props.min) / (percentage <= .5 ? 20 : 100)),
    stepUp = step ?? Math.ceil((props.max - props.min) / (percentage < .5 ? 20 : 100))

  // console.log({percentage, props, stepDown, stepUp})

  return (
    <div className={classNames(css.sliderToolbarPanel, className)}
         data-value={props.value}
         onDoubleClick={onDoubleClick}
    >
      {!hideIcons && <Tooltip title={`Zoom Out`} placement={tooltipLocation}>
		  <PrimaryIconButton icon={zoomOutIcon} onClick={() => props.onChange(null, Math.max(props.min, props.value - stepDown))}/>
	  </Tooltip>}
      <Tooltip title={tooltipTitle ?? props.value.toLocaleString()} placement={tooltipLocation}>
        <Slider step={step ?? Math.min(1, (props.max - props.min) / 10)}
                onDoubleClick={() => props.value = props.defaultValue ?? (props.max - props.min) / 2}
                {...props}
                className={css.materialSlider}
                data-type='Slider'
                classes={{thumb: css.thumb, active: css.active, markLabel: css.markLabel}}
                color='secondary'/>
      </Tooltip>
      {!hideIcons && <Tooltip title={`Zoom In`} placement={tooltipLocation}>
		  <PrimaryIconButton icon={zoomInIcon} onClick={() => props.onChange(null, Math.min(props.max, props.value + stepUp))}/>
	  </Tooltip>}
    </div>
  )
})

CardSizeSlider.defaultProps = {
  zoomOutIcon: <ZoomOutSharp/>,
  zoomInIcon: <ZoomInSharp/>
}
CardSizeSlider.css = css

export const CardSpacingSliderContextMenuItem = observer<{ showResetMenuItem?: boolean } & ContextMenuItemProps>(function CardSpacingSliderContextMenuItem({showResetMenuItem, ...props}) {
  const {settings: {viewMode}, resetCardSpacing} = useCatalog()

  return viewMode === 'card' && <Fragment>
	  <ContextMenuItem className={css.cardSizeSliderMenuItem}
	                   data-type='CardSpacingSlider'
                       {...props}
	                   text={<CardSpacingSliderCatalog/>}
	  />
    {showResetMenuItem && <ContextMenuItem text={'Reset Card Sizes'} icon='reset' onClick={resetCardSpacing}/>}
  </Fragment>
})

export const CardSizeSliderContextMenuItem = observer<{ showResetMenuItem?: boolean } & ContextMenuItemProps>(function CardSizeSliderContextMenuItem({showResetMenuItem, ...props}) {
  const {settings: {viewMode}, resetCardSize} = useCatalog()
  return viewMode === 'card' && <Fragment>
	  <ContextMenuItem className={css.cardSizeSliderMenuItem}
	                   data-type='CardSizeSlider'
                       {...props}
	                   text={<CardSizeSliderCatalog/>}
	  />
    {showResetMenuItem && <ContextMenuItem text={'Reset Card Sizes'} icon='reset' onClick={resetCardSize}/>}
  </Fragment>
})

export const CatalogColumnsSliderContextMenuItem = observer<{ dividerBefore?: boolean, dividerAfter?: boolean, showResetMenuItem?: boolean }>(function CatalogColumnsSliderContextMenuItem({showResetMenuItem, ...props}) {
  const {settings: {viewMode}, canShowColumnsSlider} = useCatalog()
  return canShowColumnsSlider && <Fragment>
	  <ContextMenuItem
        {...props}
		className={css.cardSizeSliderMenuItem}
		data-type='CardSizeSlider'
		text={<CatalogColumnSliderMaterial/>}
	  />
    {/*{false && showResetMenuItem && <ContextMenuItem text={'Reset Column Sizes'} icon='reset' onClick={catalog.resetCardSize}/>}*/}
  </Fragment>
})
export const ManageFieldsContextMenuItem = observer(function ManageFieldsContextMenuItem() {
  const {settings: {viewMode}, labelForSection, availableCardFieldBySection, cardView, cv: {toggleCardViewFieldVisible}, labelForField} = useCatalog()

  if (viewMode !== 'card') return null

  return (
    <Fragment>
      {/*<ContextMenuItem icon={icons.manageFields} text='Manage Fields (Toolbar)...'>
      <CardViewToolbarMenuPanel />
    </ContextMenuItem>

      <ContextMenuItem icon={icons.manageFields} text='Manage Fields (Raw Fields)...'>
        {visibleCardFields.map(f => <ContextMenuItem icon='tick' text={labelForField(f)} key={f.field} onClick={() => cardView[f.field] = false}/>)}
        {availableCardFields.filter(f => !cardView[f.field]).map(f => <ContextMenuItem icon='blank' text={labelForField(f)} key={f.field} onClick={() => cardView[f.field] = true}/>)}
      </ContextMenuItem>
      */}

      <ContextMenuItem icon={icons.manageFields} text='Display Fields'>
        {availableCardFieldBySection.map(({section, fields}, i) => (
          // <ContextMenuItem key={i} text={labelForSection(section)}>
          // <CollapsibleStateful title={labelForSection(section)}>
          <Fragment key={i}>
            <MenuDivider title={labelForSection(section)}/>
            {fields.map(f => <ContextMenuItem icon={cardView[f.field]?.show ? 'tick' : 'blank'} text={labelForField(f)} key={f.field} onClick={() => toggleCardViewFieldVisible(f.field)}/>)}
          </Fragment>
        ))}
      </ContextMenuItem>

    </Fragment>
  )
})

/**
 * Our states are:
 *    1.  Ungrouped
 *    2.  Grouped By levels 1/2 (can add another level of grouping)
 *    3.  Group by maximum # of levels (currently 3).  Cannot add another level of grouping.
 */
export const CatalogHierarchySubmenu = observer(function GroupByContextMenuItem() {
  return <ContextMenuItem icon={icons.groupBy} text='Group By...' shouldDismissPopover>
    <CatalogHierarchyMenuItems/>
  </ContextMenuItem>
})

export const ClearHierarchyMenuItem = observer<ContextMenuItemProps>(function ClearHierarchyMenuItem(props) {
  const {strings, isGroupedView, groupBy: {clearHierarchy}} = useCatalog()
  return isGroupedView && <ContextMenuItem {...props} text={strings.clearGroups} icon={icons.clearHierarchy} onClick={clearHierarchy}/>
})

export const GroupByCreateLevelMenuItem = observer(function GroupByCreateLevelMenuItem() {
  const {groupBy: {hierarchy}} = useCatalog()
  return (
    <ContextMenuItem icon={'header'} text={`Group By...`}>
      {_.range(0, hierarchy.length).map(level => <GroupByContextMenuItemForLevel level={level} key={level}/>)}
      <GroupByContextMenuItemContentsForLevel level={-1}/>
    </ContextMenuItem>
  )
})

export const CatalogHierarchyMenuItems = observer(function CatalogHierarchyMenuItems() {
  const {groupBy: {isProductView, hierarchy, hierarchyGroups, maxGroupDepth}} = useCatalog(),
    nextLevel = hierarchy.length
  return (
    <Fragment>
      {isProductView
       ? <GroupByContextMenuItemContentsForLevel level={-1}/>
       : <Fragment>
         {hierarchy.length < maxGroupDepth && <GroupByCreateLevelMenuItem/>}
       </Fragment>}
    </Fragment>
  )
})

export const GroupByContextMenuItemForLevel = observer<{ level: number }>(function GroupByContextMenuItemForLevel({level}) {
  const {groupBy: {hierarchyGroups}} = useCatalog()
  return (
    <ContextMenuItem icon={level == 0 ? 'header-one' : level === 1 ? 'header-two' : 'header'} text={`Group by ${hierarchyGroups[level]?.groupTitle}...`}>
      <GroupByContextMenuItemContentsForLevel level={level}/>
    </ContextMenuItem>
  )
})

export const GroupByContextMenuItemContentsForLevel = observer<{ level: number }>(function GroupByContextMenuItemForLevel({level}) {
  const {settings: {viewMode}, groupBy: {hierarchy}, toggleCardField, cardView} = useCatalog(),
    groupByGroup = useGroupByGroupStore(),
    store = useVipLocalStore((ws) => new GroupByDropdownStore(ws, {level, group: groupByGroup?.grouping}, 'contextMenu')),
    {hierarchyGroup} = store

  return (
    <GroupByDropdownStoreContext.Provider value={store}>
      {/*<CatalogItemGroupByMenuItem/>*/}
      {hierarchyGroup ? <Fragment>
        {viewMode == 'card' && _.has(cardView, hierarchyGroup.groupName) && <Fragment>
			<ContextMenuItem dividerBefore dividerAfter icon={cardView[hierarchyGroup.groupName] ? 'tick' : 'blank'} text={`Show on Cards`} onClick={() => toggleCardField(hierarchyGroup.fieldDefinition)}/>
		</Fragment>}

        <ChangeGroupContextMenuItem/>
        <ContextMenuItem text={`Remove Group`} icon='trash' onClick={() => hierarchy.splice(level, 1)}/>
        <MenuDivider/>

        {!groupByGroup && <Fragment>
			<GroupSubtitlesContextMenuItem group={hierarchyGroup}/>
			<ChooseSubgroupSubmenu/>
			<ChooseSwatchesSubmenuItem/>
		</Fragment>}
      </Fragment> : <GroupByFieldMenuItems/>}
    </GroupByDropdownStoreContext.Provider>
  )
})

export const GroupByFieldMenuItems = observer(function GroupByFieldMenuItems() {
  const {strings, groupBy: {menuStore: {hierarchyOptionMenuItems, specOptionMenuItems}, hierarchy}} = useCatalog()

  return <Fragment>
    {hierarchyOptionMenuItems?.length > 0 &&
	 <Fragment>
		 <MenuDivider title={strings.hierarchy}/>
       {hierarchyOptionMenuItems}
	 </Fragment>}
    {specOptionMenuItems?.length > 0 &&
	 <Fragment>
		 <MenuDivider title={strings.specifications}/>
       {specOptionMenuItems}
	 </Fragment>}
    <MenuDivider title={'Other Fields'}/>
    <GroupMenuItems/>
  </Fragment>
})

export const ChangeGroupContextMenuItem = observer(function f() {
  const store = useGroupByDropdownStore(),
    dropdownStore = useVipLocalStore((ws) => new GroupByDropdownStore(ws, {level: store?.grouping?.level, group: store?.grouping}, 'contextMenu'))

  if (!store) return null

  return (
    <GroupByDropdownStoreContext.Provider value={dropdownStore}>
      <ContextMenuItem text={`Change '${store.hierarchyGroup?.groupTitle}' to...`} icon={icons.changeGroup}>
        <GroupByFieldMenuItems/>
      </ContextMenuItem>
    </GroupByDropdownStoreContext.Provider>)
})

export const AggregateDetailsContextMenuItem = observer(function AggregateDetails() {
  const {
    strings, isGroupedView, features: {groupBy: {detailAggregations}}, detailAggregationFields,
    settings: {showAggDetailsWhenCollapsed, detailAggs}, labelForFieldName_currentCatalog,
    aggregation
  } = useCatalog()

  if (!aggregation) return null

  const {onToggleShowAggs, aggregateLocation, onToggleShowAggDetailsWhenCollapsed} = aggregation
  // console.log(detailAggregationFields, detailAggregations)
  return <Fragment>
    <ContextMenuItem icon={icons.aggregateDetails} text={strings.aggregateDetails} onClick={onToggleShowAggs} shouldDismissPopover={false}>
      <GroupAggregationInlineMenuItem/>
      <GroupAggregationSidebarMenuItem/>
      {aggregateLocation === 'inline' && isGroupedView && <Fragment>
		  <MenuDivider/>
		  <ContextMenuItem
			  icon={showAggDetailsWhenCollapsed ? 'small-tick' : 'blank'}
			  active={showAggDetailsWhenCollapsed}
			  shouldDismissPopover={false} text={'Show when Collapsed'}
			  onClick={onToggleShowAggDetailsWhenCollapsed}/>
	  </Fragment>}

      {!_.isEmpty(detailAggregationFields) && <Fragment>
		  <MenuDivider/>
        {detailAggregationFields.filter(f => f.hidden !== true && f.inline !== false).map((f, i) => {
          const label = f.label ?? labelForFieldName_currentCatalog(f.field),
            key = f.output ?? f.field,
            visible = detailAggs[key] !== false

          return <ContextMenuItem
            key={i}
            data-key={key}
            data-field={f.field}
            icon={visible ? 'tick' : 'blank'}
            text={label}
            onClick={() => detailAggs[key] = !visible}
          />
        })}
	  </Fragment>}
    </ContextMenuItem>
  </Fragment>
})

export const ToggleLeftSidebarContextMenuItem = observer(function CollapseLeftSidebarContextMenuItem() {
  const {flexLayout: {isMaximized, toggleKnownTab, isShowingLibraries, isShowingFilters}, sidebar: {left, left: {selectedTab, toggleOpen}}, features: {library}} = useCatalog()
  const open = isMaximized ? left.open : (selectedTab === 'libraries' ? isShowingLibraries : selectedTab === 'filters' ? isShowingFilters : false)

  if (selectedTab === 'libraries' && library?.show === false) return null

  return <Fragment>
    <ContextMenuItem
      text={`${open ? 'Close' : 'Open'} ${_.capitalize(selectedTab)} Sidebar`}
      icon={open ? icons.closeLeftSidebar : icons.openLeftSidebar}
      onClick={toggleOpen}/>
  </Fragment>
})

class GroupAggregationInlineMenuItemStore extends VipLocalStore {
  @computed get active() {
    const {catalog: {settings: {showAggs}, aggregation: {aggregateLocation}}} = this
    return showAggs //&& aggregateLocation === 'inline'
  }

  @action onClick = () => {
    //catalog.settings.aggregateLocation = 'inline'
    const {catalog} = this
    catalog.settings.showAggs = !catalog.settings.showAggs
  }
}

const GroupAggregationInlineMenuItem = observer(function f() {
  const {active, onClick} = useVipLocalStore((ws) => new GroupAggregationInlineMenuItemStore(ws))

  return <ContextMenuItem active={active} shouldDismissPopover={false} text={`Show Aggregate Details`} onClick={onClick}/>
})

class GroupAggregationSidebarMenuItemStore extends VipLocalStore {
  @computed get active() {
    const {catalog} = this
    const {settings: {showAggs}, aggregation: {aggregateLocation}} = catalog
    return aggregateLocation === 'sidebar'
  }

  @action onClick = () => {
    const {catalog} = this
    if (!this.active) {
      catalog.settings.aggregateLocation = 'sidebar'
      catalog.settings.showAggs = true
    } else {
      catalog.settings.aggregateLocation = 'inline'
    }
  }
}

const GroupAggregationSidebarMenuItem = observer(function GroupAggregationSidebarMenuItem() {
  const {active, onClick} = useVipLocalStore((ws) => new GroupAggregationSidebarMenuItemStore(ws)),
    {isProductView} = useCatalog()

  return <ContextMenuItem disabled={isProductView} active={active} shouldDismissPopover={false} text={`Show in Sidebar`} onClick={onClick}/>
})
/// endregion

// export interface ISystemContextMenuStore {
//   openInNewTab: () => void
//   openInNewWindow: () => void
//   copyLinkAddress: () => void
//   hasImage?: boolean
//   openImageInNewTab: () => void
//   saveImageAsUrl: string
//   copyImageAddress: () => void
// }

// export const SystemMenu = () => {
//   const cardStore = useBuilderCardStore(),
//     groupStore = useGroupByGroupStore()
//
//   return (cardStore || groupStore)
//          && (
//            <ContextMenuItem
//              text='More...'
//              icon='settings'>
//              <SystemLikeContextMenuItems/>
//            </ContextMenuItem>)
// }

/**
 * Attempts to replicate a natural feeling right click menu in chrome
 * See https://centric8.atlassian.net/browse/YFTS-8039
 */
// export const SystemLikeContextMenuItems = observer(function SystemLikeContextMenuItems() {
//   const {isCatalogWhitespace, isProductCard, isGroupCard, isGroupWhitespace } = contextMenu
//   const store : ISystemContextMenuStore = useBuilderCardStore() ?? useGroupByGroupStore()
//
//   return <Fragment>
//     <ContextMenuItem text='Open in New Tab' onClick={store.openInNewTab} shouldDismissPopover />
//     <ContextMenuItem text='Open in New Window' onClick={store.openInNewWindow} shouldDismissPopover/>
//     <ContextMenuItem text='Copy Link Address' onClick={store.copyLinkAddress}/>
//     {store.hasImage && <Fragment>
//         <MenuDivider/>
//      <ContextMenuItem text='Open Image in New Tab' onClick={store.openImageInNewTab} shouldDismissPopover/>
//     <ContextMenuItem download href={store.saveImageAsUrl} text='Save Image As...' shouldDismissPopover/>
//
//     <ContextMenuItem text='Copy Image Address' onClick={store.copyImageAddress} />
//     </Fragment>}
//   </Fragment>
// })

export const SelectedCatalogItemsActionContextMenuItem = observer<ContextMenuItemProps>(function SelectedCatalogItemsActionContextMenuItem(props) {
  const {selection} = useCatalog()
  if (!selection) return null

  const {selectedIds, selectedItemsLabel} = selection
  if (selectedIds.size < 2) return null

  return (//<ContextMenuItem {...props} icon='selection' text={selectedItemsLabel}>
    <SelectedCatalogItemsActionMenuItems/>
  )//</ContextMenuItem>
})

const ToolbarItemsContextMenuItem = observer(function ToolbarItemsContextMenuItem() {
  const {settings: {toolbarItems: {sliders}}, flexLayout} = useCatalog()

  return (<Fragment>
    <ContextMenuItem icon='settings' text='Toolbar Items' dividerAfter>
      {false &&
	   <Fragment>
		   <SettingsContextMenuItem setting='topToolbar' icon='tick' text='Top Toolbar'/>
		   <SettingsContextMenuItem setting='footerToolbar' icon='tick' text='Footer Toolbar'/>
	   </Fragment>}


      <SettingsContextMenuItem setting='imageSources' icon='tick' text='Image Sources'>
        <ImageSourcesMenuContent/>
      </SettingsContextMenuItem>
      <SettingsContextMenuItem setting='sliders' text='Sliders'>
        <CardSpacingSliderCatalog/>
        <CardSizeSliderCatalog/>
        <CatalogImageSourceSelectedWidgetsMenuItems/>
        <MenuDivider title='Location'/>
        <ContextMenuItem text='Header' icon={sliders.location === 'top' ? 'tick' : 'blank'} onClick={() => sliders.location = 'top'}/>
        <ContextMenuItem text='Toolbar' icon={sliders.location === 'secondary' ? 'tick' : 'blank'} onClick={() => sliders.location = 'secondary'}/>
        <ContextMenuItem text='Footer' icon={sliders.location === 'footer' ? 'tick' : 'blank'} onClick={() => sliders.location = 'footer'}/>
      </SettingsContextMenuItem>
      <SettingsContextMenuItem noSubmenuPadding setting='viewOptions' text='View Options'/>
    </ContextMenuItem>
  </Fragment>)
})

const SettingsContextMenuItem = observer<ContextMenuItemProps & { setting: keyof ShowToolbarItemsSettings }>(function SettingsContextMenuItem({setting, ...props}) {
  const {settings: {toolbarItems}} = useCatalog(),
    settingPath = `${setting}.visible`,
    v = _.get(toolbarItems, settingPath),
    onClick = useCallback(() => {
      let show = _.get(toolbarItems, settingPath)

      _.set(toolbarItems, settingPath, !show)
    }, [])

  return (<ContextMenuItem {...props} icon={v === true ? 'tick' : 'blank'} onClick={onClick}>

  </ContextMenuItem>)
})

export const FilterForCatalogFieldValue = observer<{ field: string, value: any, title?: string }>(function FilterForCatalogFieldValue({field, value, title}) {
  const groupStore = useGroupByGroupStore(),
    catalog = useCatalog(),
    {strings, vlp, es, filter} = useStores(),
    filterGroup = filter.groupFor(field),
    f = catalog._fieldByName[field]

  if (_.isEmpty(filterGroup)) {
    const {hierarchy: {parseHierarchyGroup, itemOfType}} = vlp
    const {type} = parseHierarchyGroup(field) ?? {}
    return <ContextMenuItem text={`Go to ${catalog.labelForFieldName_currentCatalog(field)} '${title}'`}
                            disabled={!groupStore || !type}
                            icon={icons.goToSeason}
                            onClick={() => groupStore.navigateTo(field)}/>
  } else {
    let v = value

    if (_.isObject(value)) {
      const filterField = filter.filterFieldFor(f)
      if (filterField) {
        // The data object being passed depends on context - it might be a swatch or it could be a builder card
        v = es.readFieldValue(v, filterField)
            ?? (!_.isBoolean(f.filter) && f.filter?.subField && es.readFieldValue(v, f.filter.subField))
            ?? es.readFieldValue(v, 'name')
      }
    }

    const {selected, excluded} = filterGroup,
      label = title ? title : _.isObject(v) ? _.has(v, filter.filterFieldFor(f)) ? es.readField(v, filter.filterFieldFor(f))?.valAsString : es.readField(v, 'name')?.valAsString : v === '' ? strings.noValue : v === null ? strings.unassigned : v,
      isSelected = selected && selected.has(v), isExcluded = excluded && excluded.has(v)
    // console.log(toJS({value, isSelected}, {recurseEverything: true}))

    // console.log({field, v, label})
    return label && <Fragment>
		<ContextMenuItem icon={isSelected ? 'tick' : 'blank'}
		                 text={`Filter for '${label}'`}
		                 onClick={() => filter.toggleFilter({key: field, value: v})}/>
		<ContextMenuItem icon={isExcluded ? 'tick' : 'blank'}
		                 text={`Exclude '${label}'`}
		                 onClick={() => filter.toggleFilter({key: field, exclude: true, value: v})}/>
	</Fragment>
  }
})

export const GroupByThisCatalogFieldMenuItem = observer<{ field: string }>(function GroupForCatalogFieldValue({field}) {
  const {groupBy, labelForFieldName_currentCatalog} = useCatalog(),
    grouping = groupBy.groupingByName.get(field),
    {isInHierarchy} = grouping ?? {}

  // console.log(toJS({value, isSelected}, {recurseEverything: true}))

  return <Fragment>
    <ContextMenuItem icon={isInHierarchy ? 'tick' : 'blank'}
                     text={`Group by '${labelForFieldName_currentCatalog(field)}'`}
                     onClick={() => groupBy.onClickGroupBy_modifyHierarchy(field)}/>
  </Fragment>
})



// WEBPACK FOOTER //
// ./src/modules/contextMenus/menus/CatalogContextMenu.tsx